import React, { useState, useEffect, useCallback } from 'react';
import { Layers, Plus, X, Star, Trash2, Pencil, RefreshCw, Lock, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { getTiers, createTier, updateTier, setDefaultTier, deleteTier, getAvailableModels } from '../api';

const BG_FIELDS = [
  { key: 'subagent_model', label: 'Subagent model', help: 'Task-tool subagents ("secondary agents") that don’t have a more specific override.' },
  { key: 'goal_loop_model', label: 'Goal / Loop model', help: 'Shared by the autonomous goal tracker and the recurring "/loop" feature.' },
  { key: 'title_model', label: 'Title model', help: 'Used to generate short session titles and similar short-text naming.' },
  { key: 'advisor_model', label: 'Fusion advisor model', help: 'Private routing advisor that chooses between lead reasoning, LF-grep, Sidekick, and Task.' },
  { key: 'review_high_model', label: 'Review model — high', help: 'Capability tier for "/review" reviewers doing correctness and error-handling work. Optional; falls back to the subagent model.', optional: true },
  { key: 'review_medium_model', label: 'Review model — medium', help: 'Capability tier for "/review" reviewers covering tests, types, and simplification. Optional; falls back to the subagent model.', optional: true },
  { key: 'review_low_model', label: 'Review model — low', help: 'Capability tier for the "/review" comment accuracy reviewer. Optional; falls back to the subagent model.', optional: true },
];
const BG_REQUIRED = BG_FIELDS.filter(({ optional }) => !optional);
const BG_EMPTY = {
  subagent_model: '', goal_loop_model: '', title_model: '', advisor_model: '',
  review_high_model: '', review_medium_model: '', review_low_model: '',
  plan_revisor_model: '',
};

const RL_FIELDS = [
  { key: 'per_minute', label: 'min' },
  { key: 'per_hour', label: 'hour' },
  { key: 'per_6h', label: '6h' },
  { key: 'per_day', label: 'day' },
];
const ZERO = { per_minute: 0, per_hour: 0, per_6h: 0, per_day: 0 };

// Build the initial per-model RL map for the editor:
// use the tier's saved per-model map if present, else seed "_default" from the
// legacy tier-wide rate_limits (smooth migration).
function initRLPM(tier) {
  const pm = tier?.rate_limits_per_model;
  if (pm && Object.keys(pm).length) {
    return Object.fromEntries(Object.entries(pm).map(([m, r]) => [m, { ...ZERO, ...r }]));
  }
  return { _default: { ...ZERO, ...(tier?.rate_limits || {}) } };
}

const BUDGET_ZERO = { per_minute: 0, per_hour: 0, per_6h: 0, per_day: 0 };
const MODEL_BUDGET_ZERO = { per_day: 0 };
const EMPTY = {
  name: '', description: '', models: [],
  rate_limits_per_model: { _default: { ...ZERO } },
  budget_limits: { ...BUDGET_ZERO },
  budget_limits_per_model: { _default: { ...MODEL_BUDGET_ZERO } },
  background_models: { ...BG_EMPTY },
  default_model: '',
  ultraplan_enabled: false,
  // Blank: no cap. A number is the cap, 0 turns live sessions off.
  realtime_sessions: '',
  // Blank: no ceiling on what one live session may spend.
  live_session_max_usd: '',
  live_fail_closed: false,
  is_default: false,
};

// Build the initial per-model budget map for the editor — same "_default" +
// per-model shape as initRLPM, just a single per_day USD field.
function initBudgetPM(tier) {
  const pm = tier?.budget_limits_per_model;
  if (pm && Object.keys(pm).length) {
    return Object.fromEntries(Object.entries(pm).map(([m, b]) => [m, { ...MODEL_BUDGET_ZERO, ...b }]));
  }
  return { _default: { ...MODEL_BUDGET_ZERO } };
}

// tier.background_models values are null when unset (API contract); <select>
// needs '' so normalize both directions at the state boundary.
function initBackgroundModels(tier) {
  const bg = tier?.background_models || {};
  return { ...BG_EMPTY, ...Object.fromEntries(Object.entries(bg).map(([k, v]) => [k, v || ''])) };
}

// Every non-optional model-role field plus the main-agent default is mandatory —
// no "not enforced" blank option. Used both for the Save-button gate and the
// tier-card "incomplete" banner.
function isTierIncomplete(t) {
  const bg = t?.background_models || {};
  if (!t?.default_model) return true;
  // Ultra-Plan has no degraded mode: enabling it without a revisor model would run the
  // planner's own model against its own plan, so the backend rejects that pairing too.
  if (t?.ultraplan_enabled && !bg.plan_revisor_model) return true;
  return BG_FIELDS.some(({ key, optional }) => !optional && !bg[key]);
}

const providerName = (provider) => provider
  .split('-')
  .map((part) => part ? part[0].toUpperCase() + part.slice(1) : part)
  .join(' ');

function groupModelOptions(ids, modelById) {
  const groups = new Map();
  ids.forEach((id) => {
    const provider = modelById.get(id).provider;
    if (!groups.has(provider)) groups.set(provider, []);
    groups.get(provider).push(id);
  });
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function ModelSelectOptions({ ids, modelById }) {
  return groupModelOptions(ids, modelById).map(([provider, providerModels]) => (
    <optgroup key={provider} label={providerName(provider)}>
      {providerModels.map((id) => <option key={id} value={id}>{id} — {providerName(provider)}</option>)}
    </optgroup>
  ));
}

// What the live-session fields would be refused for, said before the save rather than after.
export function liveLimitProblem(f) {
  const sessions = f.realtime_sessions;
  if (sessions !== '' && !(Number.isInteger(Number(sessions)) && Number(sessions) >= 0)) {
    return 'Live sessions at once must be a whole number, 0 or more (blank for unlimited)';
  }
  const ceiling = f.live_session_max_usd;
  if (ceiling !== '' && !(Number(ceiling) > 0)) {
    return 'Most one live session may spend must be above $0 (blank for no ceiling)';
  }
  return null;
}

// A refused save's reason as text: a validation error's `detail` is a list, and rendered as a
// child it blanked the page (review #335).
export function saveError(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => `${(d.loc || []).filter((p) => p !== 'body').join('.')}: ${d.msg}`)
      .join('; ');
  }
  return 'Save failed';
}

function TierModal({ tier, models, onClose, onSaved }) {
  const modelIds = models.map((model) => model.id);
  const modelIdSet = new Set(modelIds);
  const modelById = new Map(models.map((model) => [model.id, model]));
  const unavailableModelCount = tier
    ? (tier.models || []).filter((id) => !modelIdSet.has(id)).length
    : 0;
  const [f, setF] = useState(() => {
    if (!tier) return EMPTY;
    const backgroundModels = initBackgroundModels(tier);
    return {
      name: tier.name,
      description: tier.description || '',
      models: (tier.models || []).filter((id) => modelIdSet.has(id)),
      rate_limits_per_model: initRLPM(tier),
      budget_limits: { ...BUDGET_ZERO, ...(tier.budget_limits || {}) },
      budget_limits_per_model: initBudgetPM(tier),
      background_models: Object.fromEntries(
        Object.entries(backgroundModels).map(([key, id]) => [key, modelIdSet.has(id) ? id : '']),
      ),
      default_model: modelIdSet.has(tier.default_model) ? tier.default_model : '',
      ultraplan_enabled: !!tier.ultraplan_enabled,
      realtime_sessions: tier.realtime_sessions ?? '',
      live_session_max_usd: tier.live_session_max_usd ?? '',
      live_fail_closed: !!tier.live_fail_closed,
      is_default: tier.is_default,
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [modelQuery, setModelQuery] = useState('');
  const [modelSelectionReviewed, setModelSelectionReviewed] = useState(unavailableModelCount === 0);
  const filteredModels = models.filter((model) => {
    const query = modelQuery.trim().toLowerCase();
    return !query || model.id.toLowerCase().includes(query) || providerName(model.provider).toLowerCase().includes(query);
  });

  // Shared by toggling one model and "select all"/"clear all" — recomputes the
  // background-model/default-model selects against the NEW allow-list so a
  // pick that just fell outside it doesn't sit there waiting to 422 on save.
  const setModels = (nextModels) => {
    setModelSelectionReviewed(true);
    setF((s) => {
      const nextBg = Object.fromEntries(
        Object.entries(s.background_models).map(([k, v]) => [k, v && !nextModels.includes(v) ? '' : v]),
      );
      const nextDefaultModel = s.default_model && !nextModels.includes(s.default_model) ? '' : s.default_model;
      return { ...s, models: nextModels, background_models: nextBg, default_model: nextDefaultModel };
    });
  };
  const toggleModel = (id) => {
    setModels(f.models.includes(id) ? f.models.filter((m) => m !== id) : [...f.models, id]);
  };
  const allModelsSelected = modelIds.length > 0 && modelIds.every((id) => f.models.includes(id));
  const toggleSelectAll = () => setModels(allModelsSelected ? [] : modelIds);
  const getRow = (m) => f.rate_limits_per_model[m] || { ...ZERO };
  const setCell = (m, k, v) => setF((s) => ({
    ...s, rate_limits_per_model: { ...s.rate_limits_per_model, [m]: { ...getRow(m), [k]: parseInt(v || 0, 10) || 0 } },
  }));
  const setBudget = (k, v) => setF((s) => ({ ...s, budget_limits: { ...s.budget_limits, [k]: parseFloat(v) || 0 } }));
  const getModelBudget = (m) => f.budget_limits_per_model[m] || { ...MODEL_BUDGET_ZERO };
  const setModelBudget = (m, v) => setF((s) => ({
    ...s, budget_limits_per_model: { ...s.budget_limits_per_model, [m]: { per_day: parseFloat(v) || 0 } },
  }));
  const setBackgroundModel = (k, v) => setF((s) => ({ ...s, background_models: { ...s.background_models, [k]: v } }));

  // rows shown in the RL editor: the catch-all _default, then each selected model
  const rlRows = ['_default', ...f.models];

  const incomplete = isTierIncomplete(f);

  const submit = async () => {
    if (!f.name.trim()) { setError('Name is required'); return; }
    if (!f.models.length) { setError('Select at least one allowed model'); return; }
    if (!modelSelectionReviewed) { setError('Review the updated allowed-model selection before saving'); return; }
    if (incomplete) {
      setError(
        f.ultraplan_enabled && !f.background_models.plan_revisor_model
          ? 'Ultra-Plan needs a plan revisor model'
          : 'Subagent, Goal/Loop, Title, Fusion Advisor, and Default main-agent model are all required',
      );
      return;
    }
    const liveProblem = liveLimitProblem(f);
    if (liveProblem) { setError(liveProblem); return; }
    setBusy(true); setError(null);
    try {
      const rlpm = {};
      rlRows.forEach((m) => { rlpm[m] = getRow(m); });
      const bpm = {};
      rlRows.forEach((m) => { bpm[m] = getModelBudget(m); });
      const payload = {
        name: f.name, description: f.description, models: f.models,
        rate_limits_per_model: rlpm,
        rate_limits: rlpm._default,   // legacy mirror for back-compat
        budget_limits: f.budget_limits,
        budget_limits_per_model: bpm,
        background_models: Object.fromEntries(Object.entries(f.background_models).map(([k, v]) => [k, v || null])),
        default_model: f.default_model || null,
        ultraplan_enabled: f.ultraplan_enabled,
        realtime_sessions: f.realtime_sessions === '' ? null : Number(f.realtime_sessions),
        live_session_max_usd: f.live_session_max_usd === '' ? null : Number(f.live_session_max_usd),
        live_fail_closed: f.live_fail_closed,
        is_default: f.is_default,
      };
      if (tier) await updateTier(tier.tier_id, payload); else await createTier(payload);
      onSaved();
    } catch (e) { setError(saveError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-6 pt-6 pb-5 shrink-0">
          <h2 className="text-xl font-semibold text-fg">{tier ? 'Edit tier' : 'New tier'}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4 overflow-y-auto px-6 pb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fg-muted mb-1">Name *</label>
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="e.g. Free, Pro" />
            </div>
            <div>
              <label className="block text-sm text-fg-muted mb-1">Description</label>
              <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="Optional" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-sm text-fg-muted">
                Allowed models * ({f.models.length} selected)
              </label>
              <div className="relative w-52">
                <Search className="w-3.5 h-3.5 text-fg-muted absolute left-2 top-1/2 -translate-y-1/2" />
                <input value={modelQuery} onChange={(e) => setModelQuery(e.target.value)} placeholder="Search model or provider"
                  className="w-full pl-7 pr-2 py-1.5 bg-field border border-field-border rounded text-fg text-xs focus:border-orange" />
              </div>
            </div>
            {unavailableModelCount > 0 && !modelSelectionReviewed && (
              <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                <span>{unavailableModelCount} previously allowed model{unavailableModelCount === 1 ? ' no longer has' : 's no longer have'} a provider and {unavailableModelCount === 1 ? 'was' : 'were'} removed from this form.</span>
                <button type="button" onClick={() => setModelSelectionReviewed(true)} className="shrink-0 font-medium hover:underline">
                  Acknowledge
                </button>
              </div>
            )}
            <div className="flex justify-end mb-1">
              <label className="flex items-center gap-2 px-1 py-1 cursor-pointer text-sm text-fg">
                <input type="checkbox" checked={allModelsSelected} onChange={toggleSelectAll} className="accent-orange" />
                <span className="font-medium">Select all</span>
              </label>
            </div>
            <div className="max-h-48 overflow-y-auto border border-field-border rounded-lg p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {models.length === 0 && <div className="text-fg-muted text-sm p-2">No models with a provider — configure them in Platform Settings.</div>}
              {models.length > 0 && filteredModels.length === 0 && <div className="text-fg-muted text-sm p-2">No matching models.</div>}
              {filteredModels.map((model) => (
                <label key={model.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-fg/5 cursor-pointer text-sm text-fg min-w-0">
                  <input type="checkbox" checked={f.models.includes(model.id)} onChange={() => toggleModel(model.id)} className="accent-orange mt-0.5" />
                  <span className="min-w-0">
                    <span className="block font-mono text-xs truncate" title={model.id}>{model.id}</span>
                    <span className="block text-[11px] text-fg-muted">Provider: {providerName(model.provider)}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* background/routing model overrides — hard enforced, not a suggestion */}
          <div>
            <label className="flex items-center gap-1.5 text-sm text-fg-muted mb-1">
              <Lock className="w-3.5 h-3.5" /> Background and routing models (enforced) *
            </label>
            <p className="text-xs text-fg-muted mb-2">
              The client <span className="text-fg">must</span> use this model for the task below — it is not a
              suggestion the client can override locally. All are required except the review tiers, which fall back
              to the subagent model when left unset.
            </p>
            {BG_FIELDS.map(({ key, label, help, optional }) => (
              <div key={key} className="mb-2">
                <span className="text-xs text-fg">{label}</span>
                <select
                  required={!optional}
                  value={f.background_models[key]}
                  onChange={(e) => setBackgroundModel(key, e.target.value)}
                  className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono"
                >
                  <option value="" disabled={!optional}>{optional ? 'Use the subagent model' : 'Select a model…'}</option>
                  <ModelSelectOptions ids={f.models} modelById={modelById} />
                </select>
                <p className="text-[11px] text-fg-muted mt-0.5">{help}</p>
              </div>
            ))}
          </div>

          {/* main-agent session model — soft default: pre-selected on first connect
              and the fallback target on error, but never restricts what the user
              picks for their own session afterward */}
          <div>
            <label className="block text-sm text-fg-muted mb-1">Default main-agent model *</label>
            <p className="text-xs text-fg-muted mb-2">
              Required. Pre-selected the first time a new user opens latentcode/LatentWork, and the model a
              session automatically falls back to if its current model errors out. Does not restrict which model
              users pick for their own sessions afterward — they remain free to switch anytime, same as today.
            </p>
            <select
              required
              value={f.default_model}
              onChange={(e) => setF((s) => ({ ...s, default_model: e.target.value }))}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono"
            >
              <option value="" disabled>Select a model…</option>
              <ModelSelectOptions ids={f.models} modelById={modelById} />
            </select>
          </div>

          {/* Ultra-Plan — an independent model checks the finished plan against the codebase
              before the user approves it. The revisor model is required whenever the mode is
              on: with none, the check would run the planner's own model against its own plan. */}
          <div>
            <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
              <input
                type="checkbox"
                checked={f.ultraplan_enabled}
                onChange={(e) => setF((s) => ({ ...s, ultraplan_enabled: e.target.checked }))}
                className="accent-orange"
              />
              Enable Ultra-Plan mode
            </label>
            <p className="text-xs text-fg-muted mt-1 mb-2">
              Adds a planning mode in which an independent model checks the finished plan against the codebase and
              reports the claims the code contradicts, before the user approves it. Costs one extra model pass per
              plan. When off, the mode does not appear in the client at all.
            </p>
            {f.ultraplan_enabled && (
              <div>
                <span className="text-xs text-fg">Plan revisor model *</span>
                <select
                  required
                  value={f.background_models.plan_revisor_model}
                  onChange={(e) => setBackgroundModel('plan_revisor_model', e.target.value)}
                  className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono"
                >
                  <option value="" disabled>Select a model…</option>
                  <ModelSelectOptions ids={f.models} modelById={modelById} />
                </select>
                <p className="text-[11px] text-fg-muted mt-0.5">
                  Required. Pick the most <span className="text-fg">capable</span> model you can afford — the check is
                  mostly careful reading of code, and that is where capability pays. All else equal, prefer a different
                  vendor to the default main-agent model above, so the reviewer does not inherit the same blind spots.
                </p>
              </div>
            )}
          </div>

          {/* per-model rate limits */}
          <div>
            <label className="block text-sm text-fg-muted mb-2">Rate limits per model (requests per window — 0 = unlimited)</label>
            <div className="border border-field-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-fg-muted bg-field/40">
                    <th className="text-left px-3 py-2 font-medium">Model</th>
                    {RL_FIELDS.map((r) => <th key={r.key} className="px-2 py-2 font-medium">{r.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rlRows.map((m) => (
                    <tr key={m} className="border-t border-field-border">
                      <td className="px-3 py-1.5 font-mono text-xs text-fg">
                        {m === '_default' ? <span className="text-fg-muted italic">default (any other model)</span> : m}
                      </td>
                      {RL_FIELDS.map((r) => (
                        <td key={r.key} className="px-1 py-1">
                          <input type="number" min="0" value={getRow(m)[r.key]} onChange={(e) => setCell(m, r.key, e.target.value)}
                            className="w-16 px-2 py-1 bg-field border border-field-border rounded text-fg text-xs text-center" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-fg-muted mt-1">A model with its own row uses those limits; everything else falls back to <span className="text-fg">default</span>.</p>
          </div>

          {/* budget limits (USD spend caps per window) */}
          <div>
            <label className="block text-sm text-fg-muted mb-2">Budget limits (USD spend per window — 0 = unlimited)</label>
            <div className="grid grid-cols-4 gap-2">
              {RL_FIELDS.map((r) => (
                <div key={r.key}>
                  <div className="text-[11px] text-fg-muted mb-1 text-center">{r.label}</div>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-muted text-xs">$</span>
                    <input type="number" min="0" step="0.01" value={f.budget_limits[r.key]}
                      onChange={(e) => setBudget(r.key, e.target.value)}
                      className="w-full pl-5 pr-2 py-1.5 bg-field border border-field-border rounded text-fg text-xs text-center" />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-fg-muted mt-1">Once spend in a window reaches its cap, requests get <span className="text-fg">402 Budget exceeded</span> until the window resets.</p>
          </div>

          {/* live (/v1/realtime) sessions one caller may hold open at once */}
          <div>
            <label className="block text-sm text-fg-muted mb-2">Live sessions at once</label>
            <input type="number" min="0" step="1" value={f.realtime_sessions}
              placeholder="unlimited"
              onChange={(e) => setF((s) => ({ ...s, realtime_sessions: e.target.value.trim() }))}
              className="w-40 px-2 py-1.5 bg-field border border-field-border rounded text-fg text-xs" />
            <p className="text-xs text-fg-muted mt-1">Per caller. Blank = unlimited, 0 = none. A live session spends continuously and a budget only sees it a turn at a time, so this caps how far ahead of the budget it can get.</p>
          </div>

          {/* what one live session may spend, counted by the session itself */}
          <div>
            <label className="block text-sm text-fg-muted mb-2">Most one live session may spend (USD)</label>
            <div className="relative w-40">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-muted text-xs">$</span>
              <input type="number" min="0.01" step="0.01" value={f.live_session_max_usd}
                placeholder="no ceiling"
                onChange={(e) => setF((s) => ({ ...s, live_session_max_usd: e.target.value.trim() }))}
                className="w-full pl-5 pr-2 py-1.5 bg-field border border-field-border rounded text-fg text-xs" />
            </div>
            <p className="text-xs text-fg-muted mt-1">The session ends with <span className="text-fg">402</span> once it has spent this much. Counted by the session, so it holds even when budgets cannot be read.</p>
            <label className="flex items-center gap-2 text-sm text-fg cursor-pointer mt-3">
              <input type="checkbox" checked={f.live_fail_closed}
                onChange={(e) => setF((s) => ({ ...s, live_fail_closed: e.target.checked }))}
                className="accent-orange" />
              Refuse live sessions when limits cannot be checked
            </label>
            <p className="text-xs text-fg-muted mt-1">Off: a Redis outage admits live sessions uncounted, as chat does. On: they are refused with 503 until it is back.</p>
          </div>

          {/* per-model daily budget (USD) */}
          <div>
            <label className="block text-sm text-fg-muted mb-2">Budget limits per model (USD spend per day — 0 = unlimited)</label>
            <div className="border border-field-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-fg-muted bg-field/40">
                    <th className="text-left px-3 py-2 font-medium">Model</th>
                    <th className="px-2 py-2 font-medium">day</th>
                  </tr>
                </thead>
                <tbody>
                  {rlRows.map((m) => (
                    <tr key={m} className="border-t border-field-border">
                      <td className="px-3 py-1.5 font-mono text-xs text-fg">
                        {m === '_default' ? <span className="text-fg-muted italic">default (any other model)</span> : m}
                      </td>
                      <td className="px-1 py-1">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted text-xs">$</span>
                          <input type="number" min="0" step="0.01" value={getModelBudget(m).per_day}
                            onChange={(e) => setModelBudget(m, e.target.value)}
                            className="w-24 pl-5 pr-2 py-1 bg-field border border-field-border rounded text-fg text-xs text-center" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-fg-muted mt-1">Enforced independently of (in addition to) the tier-wide budget above — whichever cap is hit first blocks the request.</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-fg-muted cursor-pointer">
            <input type="checkbox" checked={f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} className="accent-orange" />
            Default tier (new users get this)
          </label>

          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-panel-border bg-panel/95 shrink-0">
          <button onClick={submit} disabled={busy || incomplete || !modelSelectionReviewed} title={!modelSelectionReviewed ? 'Review the updated allowed-model selection first' : incomplete ? 'Set all four required model fields first' : undefined} className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm disabled:opacity-50">
            {busy ? 'Saving…' : (tier ? 'Save changes' : 'Create tier')}
          </button>
          <button onClick={onClose} className="px-6 py-2.5 bg-panel border border-panel-border text-fg rounded-lg hover:border-orange text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function rlSummary(tier) {
  // prefer per-model; fall back to legacy tier-wide
  const pm = tier.rate_limits_per_model && Object.keys(tier.rate_limits_per_model).length
    ? tier.rate_limits_per_model : { _default: tier.rate_limits || {} };
  return pm;
}

export default function Tiers() {
  const [tiers, setTiers] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [expandedTierId, setExpandedTierId] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([getTiers(), getAvailableModels()]);
      setTiers(t.data.tiers || []);
      setModels((m.data.data || [])
        .filter((model) => typeof model.provider === 'string' && model.provider.trim())
        .map((model) => ({ id: model.id, provider: model.provider.trim() })));
    } catch { setMsg({ type: 'error', text: 'Failed to load tiers' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (text) => { setMsg({ type: 'success', text }); setTimeout(() => setMsg(null), 3500); };
  const makeDefault = async (t) => { await setDefaultTier(t.tier_id); flash(`${t.name} is now the default tier`); load(); };
  const remove = async (t) => { if (!window.confirm(`Delete tier "${t.name}"?`)) return; try { await deleteTier(t.tier_id); flash('Tier deleted'); load(); } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed' }); } };
  const modelById = new Map(models.map((model) => [model.id, model]));

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-fg">Tiers</h1>
          <p className="text-fg-muted text-sm mt-1">Bundle allowed models + per-model rate limits, then attach to users or projects. New users get the default tier.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(null)} className="flex items-center gap-2 px-3 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm"><Plus className="w-4 h-4" />New tier</button>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg text-sm"><RefreshCw className="w-4 h-4" />Refresh</button>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex justify-between ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{msg.text}</span><button onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-fg-muted py-16">Loading…</div>
      ) : tiers.length === 0 ? (
        <div className="text-center text-fg-muted py-16 bg-panel border border-panel-border rounded-xl">No tiers yet — create one. The first tier becomes the default.</div>
      ) : (
        <div className="space-y-3">
          {tiers.map((t) => {
            const pm = rlSummary(t);
            const expanded = expandedTierId === t.tier_id;
            const configuredBackgroundModels = BG_REQUIRED.filter(({ key }) => t.background_models?.[key]).length;
            return (
              <div key={t.tier_id} className="bg-panel border border-panel-border rounded-xl overflow-hidden">
                <div className="flex items-start justify-between gap-4 p-4">
                  <button
                    type="button"
                    onClick={() => setExpandedTierId(expanded ? null : t.tier_id)}
                    aria-expanded={expanded}
                    className="flex items-start gap-3 text-left min-w-0 flex-1"
                  >
                    <span className="mt-0.5 p-1 rounded bg-orange/10 text-orange">
                      {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 flex-wrap">
                        <Layers className="w-4 h-4 text-orange" />
                        <span className="text-fg font-semibold">{t.name}</span>
                        {t.is_default && <span className="px-2 py-0.5 bg-orange/20 text-orange text-xs rounded inline-flex items-center gap-1"><Star className="w-3 h-3" />Default</span>}
                        {isTierIncomplete(t) && <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-xs rounded">Incomplete</span>}
                      </span>
                      {t.description && <span className="block text-fg-muted text-sm mt-1 truncate">{t.description}</span>}
                    </span>
                  </button>
                  <div className="flex items-center gap-1">
                    {!t.is_default && <button onClick={() => makeDefault(t)} title="Set default" className="p-1.5 text-fg-muted hover:text-orange rounded"><Star className="w-4 h-4" /></button>}
                    <button onClick={() => setEditing(t)} title="Edit" className="p-1.5 text-fg-muted hover:text-fg rounded"><Pencil className="w-4 h-4" /></button>
                    {t.is_default ? (
                      <div className="relative group">
                        <button
                          disabled
                          className="p-1.5 text-fg-muted/40 rounded cursor-not-allowed"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="pointer-events-none absolute right-0 top-full mt-1 w-52 rounded-lg bg-fg text-surface text-xs px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-75 z-10">
                          Can't delete the default tier - set another tier as default first
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => remove(t)} title="Delete" className="p-1.5 text-fg-muted hover:text-red-400 rounded"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 pb-4 text-xs">
                  <div><span className="block text-fg-muted">Allowed models</span><span className="text-fg">{t.models?.length ? t.models.length : 'All'}</span></div>
                  <div><span className="block text-fg-muted">Rate policies</span><span className="text-fg">{Object.keys(pm).length}</span></div>
                  <div className="min-w-0"><span className="block text-fg-muted">Default model</span><span className="block text-fg font-mono truncate" title={t.default_model || 'Not set'}>{t.default_model || 'Not set'}</span></div>
                  <div><span className="block text-fg-muted">Background models</span><span className={configuredBackgroundModels === BG_REQUIRED.length ? 'text-fg' : 'text-red-400'}>{configuredBackgroundModels}/{BG_REQUIRED.length} configured</span></div>
                </div>

                {expanded && (
                  <div className="border-t border-panel-border p-4 grid grid-cols-1 xl:grid-cols-2 gap-5 bg-surface/30">
                    <div>
                      <div className="text-xs text-fg-muted mb-2">Models ({t.models?.length || 'all'})</div>
                      <div className="max-h-40 overflow-y-auto flex flex-wrap content-start gap-1.5 pr-1">
                        {(t.models || []).length === 0
                          ? <span className="text-fg-muted text-xs">Unrestricted — all configured models allowed</span>
                          : t.models.map((modelId) => (
                            <span key={modelId} className="px-2 py-1 bg-orange/10 text-orange text-xs rounded min-w-0">
                              <span className="font-mono">{modelId}</span>
                              <span className="ml-1.5 text-fg-muted">· {providerName(modelById.get(modelId).provider)}</span>
                            </span>
                          ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-fg-muted mb-2">Rate limits per model</div>
                      <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                        {Object.entries(pm).map(([m, rl]) => (
                          <div key={m} className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs text-fg w-44 truncate" title={m === '_default' ? 'default' : m}>{m === '_default' ? 'default' : m}</span>
                            {RL_FIELDS.map((r) => (
                              <span key={r.key} className="px-1.5 py-0.5 bg-field border border-field-border rounded text-xs text-fg-muted">
                                {r.label}: {rl?.[r.key] ? rl[r.key] : '∞'}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-fg-muted mb-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Background and routing models (enforced)</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-1.5">
                        {BG_FIELDS.map(({ key, label, optional }) => {
                          const value = t.background_models?.[key];
                          const fallback = optional ? 'Subagent model' : 'Not set';
                          return (
                            <div key={key} className="flex items-center justify-between gap-3 text-xs">
                              <span className="text-fg-muted">{label}</span>
                              <span className={`font-mono truncate ${value ? 'text-fg' : optional ? 'text-fg-muted' : 'text-red-400'}`} title={value || fallback}>
                                {value || fallback}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-fg-muted mb-2 flex items-center gap-1"><Lock className="w-3 h-3" /> Ultra-Plan</div>
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-fg-muted">Plan revisor model</span>
                        {t.ultraplan_enabled ? (
                          <span
                            className={`font-mono truncate ${t.background_models?.plan_revisor_model ? 'text-fg' : 'text-red-400'}`}
                            title={t.background_models?.plan_revisor_model || 'Not set'}
                          >
                            {t.background_models?.plan_revisor_model || 'Not set'}
                          </span>
                        ) : (
                          <span className="text-fg-muted">Disabled</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="text-fg-muted">Live sessions at once</span>
                      <span className="text-fg">
                        {t.realtime_sessions == null
                          ? 'Unlimited'
                          : t.realtime_sessions === 0 ? 'None (off)' : t.realtime_sessions}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="text-fg-muted">Per live session</span>
                      <span className="text-fg">
                        {t.live_session_max_usd == null ? 'No ceiling' : `$${t.live_session_max_usd}`}
                        {t.live_fail_closed ? ' · fails closed' : ''}
                      </span>
                    </div>

                    {isTierIncomplete(t) && (
                      <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 text-red-400 text-xs rounded-lg self-start">
                        Set all required model fields before this tier is complete.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing !== undefined && (
        <TierModal tier={editing} models={models}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); flash('Tier saved'); load(); }} />
      )}
    </div>
  );
}
